/**
 * Тематика: типы с историей Wordstat за 24 месяца и фразы, из которых их
 * заводят.
 *
 * Сбор Wordstat идёт в reels-worker минуту-две. Пока у тематики есть задача в
 * очереди или в работе, экран переспрашивает сервис раз в 3 с: ответ тематики
 * несёт и задачи, и типы, поэтому, когда сбор закончится, свежие цифры придут
 * тем же запросом, отдельный перезапрос не нужен.
 *
 * Подборка (тематика с владельцем) — тот же экран, но типы зовутся словами,
 * а затравки может не быть. Чужую подборку смотрят без кнопок: правит только
 * владелец (сервис ответил бы 403).
 *
 * Кнопка «Собрать все референсы (ru + en)» ставит пакет: воркер сам переводит
 * фразы и ищет по каждому действующему слову на двух языках (≈7 мин на поиск).
 * Прогресс пакета идёт из счётчиков `t.batch` — задач в `t.tasks` только 20,
 * и дочерние пакета их вытесняют.
 *
 * Главное на экране — таблица слов, поэтому она первая: слова добавляются
 * кнопкой «+» в окне (сразу пачкой), сбор Pinterest по слову — кнопкой
 * «собрать» в строке, задачи сбора и фразы Wordstat — ниже.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Images, Loader2, Plus, RefreshCw, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";

import { AddWordForm } from "@/components/trends/AddWordForm";
import { BatchBar } from "@/components/trends/BatchBar";
import { Dialog, DialogFooter } from "@/components/trends/Dialog";
import { PhrasesPanel } from "@/components/trends/PhrasesPanel";
import { TaskList } from "@/components/trends/TaskList";
import { TypesTable } from "@/components/trends/TypesTable";
import { btn, btnGhost, btnPrimary, Empty, ErrorLine, Loading, Panel } from "@/components/trends/ui";
import { BackLink } from "@/shared/ui";
import { plural } from "@/lib/library";
import { useIdentity } from "@/lib/identity";
import { num, when } from "@/lib/media";
import {
  canEdit,
  isBatchActive,
  isTaskActive,
  loadTopic,
  POLL_MS,
  pollWhileActive,
  post,
  retryQuery,
  ruDate,
  trendKeys,
  type TypeState,
} from "@/lib/trends";

export function TrendTopicPage() {
  const topicId = Number(useParams().topicId);
  const queryClient = useQueryClient();
  const me = useIdentity().data?.id;
  const [notice, setNotice] = useState("");
  const [adding, setAdding] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);

  // Отметка «добавлено» гаснет сама: слово уже видно в таблице.
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(""), 6000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const topic = useQuery({
    queryKey: trendKeys.topic(topicId),
    queryFn: () => loadTopic(topicId),
    retry: retryQuery,
    enabled: Number.isFinite(topicId) && topicId > 0,
    refetchInterval: (q) =>
      pollWhileActive(q.state.data?.tasks) || (isBatchActive(q.state.data?.batch) ? POLL_MS : false),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: trendKeys.topic(topicId) });

  const collect = useMutation({
    mutationFn: () => post<{ task_id: number }>(`/api/trends/${topicId}/collect`, {}),
    onSuccess: async () => {
      setNotice("");
      await refresh();
      void queryClient.invalidateQueries({ queryKey: trendKeys.list });
    },
  });

  const state = useMutation({
    mutationFn: ({ id, value }: { id: number; value: TypeState }) =>
      post<{ state: TypeState }>(`/api/trend-types/${id}/state`, { state: value }),
    onSuccess: () => refresh(),
  });

  // Сбор Pinterest по слову с настройками сервиса по умолчанию (`{}`).
  const pinterest = useMutation({
    mutationFn: (id: number) => post<{ task_id: number }>(`/api/trend-types/${id}/pinterest`, {}),
    onSuccess: (_got, id) => {
      void refresh();
      void queryClient.invalidateQueries({ queryKey: trendKeys.type(id) });
    },
  });

  const pinterestAll = useMutation({
    mutationFn: () => post<{ task_id: number }>(`/api/trends/${topicId}/pinterest-all`, {}),
    onSuccess: async () => {
      setConfirmAll(false);
      await refresh();
    },
  });

  // Ошибка прошлой попытки не должна встречать при следующем открытии окна.
  const closeConfirmAll = () => {
    setConfirmAll(false);
    pinterestAll.reset();
  };

  const cancelBatch = useMutation({
    mutationFn: (taskId: number) => post<{ cancelled: number }>(`/api/trends/batches/${taskId}/cancel`, {}),
    onSuccess: () => refresh(),
  });

  const phraseEn = useMutation({
    mutationFn: ({ id, text }: { id: number; text: string }) =>
      post<{ phrase_en: string | null }>(`/api/trend-types/${id}/phrase-en`, { phrase_en: text }),
    onSuccess: () => refresh(),
  });

  if (!Number.isFinite(topicId) || topicId <= 0)
    return (
      <div className="p-5">
        <ErrorLine error={new Error("Неверный адрес тематики.")} />
      </div>
    );
  if (topic.isPending) return <Loading />;
  if (topic.isError)
    return (
      <div className="grid gap-3 p-5">
        <BackLink to="/trends">Все тематики</BackLink>
        <ErrorLine error={topic.error} />
      </div>
    );

  const t = topic.data;
  const active = t.tasks.filter(isTaskActive);
  const wordstatActive = active.some((x) => x.kind === "wordstat");
  const personal = t.owner_user_id !== 0;
  const editable = canEdit(t, me);
  const batchActive = isBatchActive(t.batch);
  const batchMinutes = t.batch_todo * 7;
  const activeTypes = t.types.filter((x) => x.state === "active").length;
  // Без затравки Wordstat снимает только историю слов — значит, нужны слова.
  const canCollect = Boolean(t.phrase) || activeTypes > 0;
  const collectingIds = new Set(
    active.filter((x) => x.kind === "pinterest" && x.query_id !== null).map((x) => x.query_id as number),
  );
  if (pinterest.isPending && pinterest.variables !== undefined) collectingIds.add(pinterest.variables);
  const onAdded = (names: string[]) => {
    const one = names.length === 1;
    const what = personal
      ? one
        ? `Слово «${names[0]}» добавлено`
        : `Добавлено ${names.length} ${plural(names.length, "слово", "слова", "слов")}`
      : one
        ? `Тип «${names[0]}» добавлен`
        : `Добавлено ${names.length} ${plural(names.length, "тип", "типа", "типов")}`;
    setNotice(`${what}. История спроса приедет после «Обновить из Wordstat».`);
    void refresh();
  };
  const addButton = (
    <button type="button" className={btnPrimary} onClick={() => setAdding(true)}>
      <Plus size={14} />
      {personal ? "Слово" : "Тип"}
    </button>
  );

  return (
    <div className="grid gap-4 p-5">
      <BackLink to="/trends">Все тематики</BackLink>

      <header className="flex flex-wrap items-start gap-4" data-annot="шапка тематики">
        <div className="min-w-0">
          <h1 className="text-[18px] font-semibold tracking-[-0.01em] text-fg">{t.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-fg-muted">
            {personal ? (
              <span className="rounded-[4px] bg-accent-dim px-1.5 text-[11.5px] text-accent-text">
                {editable ? "моя подборка" : `подборка · ${t.owner_name ?? "коллеги"}`}
              </span>
            ) : null}
            {t.phrase ? (
              <span>
                затравка <span className="text-fg">«{t.phrase}»</span>
              </span>
            ) : (
              <span className="text-fg-faint">без затравки Wordstat</span>
            )}
            {t.phrase ? (
              <span title="Частота затравки в таблице Wordstat за её период">
                частота <span className="tnum text-fg">{num(t.total)}</span>
                {t.table_from || t.table_to ? (
                  <span className="text-fg-faint">
                    {" "}
                    за {ruDate(t.table_from)} — {ruDate(t.table_to)}
                  </span>
                ) : null}
              </span>
            ) : null}
            <span>
              собирали{" "}
              <span className="tnum text-fg">{t.date_collect ? when(t.date_collect) : "ещё нет"}</span>
            </span>
          </div>
          {t.note ? <p className="mt-1 text-[12.5px] text-fg-muted">{t.note}</p> : null}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {active.length ? (
            <span className="inline-flex items-center gap-1.5 rounded-[var(--radius-ui)] bg-warn-bg px-2 py-1 text-[12px] text-warn">
              <Loader2 size={12} className="animate-spin" />
              {wordstatActive ? "Сбор Wordstat идёт — страница обновится сама" : "Идёт сбор — страница обновится сама"}
            </span>
          ) : null}
          {t.refs > 0 ? (
            <Link
              to={`/trends/${t.id}/refs`}
              className={btn}
              title="Все пины всех слов одним списком: фильтры, теги, выделение и пакетное «в библиотеку»"
            >
              <Images size={13} />
              Все референсы · {num(t.refs)}
            </Link>
          ) : null}
          {editable ? (
            <button
              type="button"
              className={btn}
              onClick={() => setConfirmAll(true)}
              disabled={batchActive || t.batch_todo === 0}
              title={
                batchActive
                  ? "Пакет сбора уже идёт"
                  : t.batch_todo === 0
                    ? "Всё уже собрано"
                    : "Собрать референсы Pinterest по всем действующим словам: русский и английский запрос"
              }
            >
              <Search size={13} />
              Собрать все референсы (ru + en)
            </button>
          ) : null}
          {editable ? (
            <button
              type="button"
              className={btn}
              onClick={() => collect.mutate()}
              disabled={collect.isPending || wordstatActive || !canCollect}
              title={
                canCollect
                  ? t.phrase
                    ? "Поставить в очередь сбор Wordstat: фразы затравки и история всех действующих слов"
                    : "Поставить в очередь сбор Wordstat: история всех действующих слов"
                  : "Сначала добавьте слово: без затравки Wordstat собирать нечего"
              }
            >
              <RefreshCw size={13} className={collect.isPending ? "animate-spin" : ""} />
              Обновить из Wordstat
            </button>
          ) : null}
        </div>
      </header>

      {editable ? null : (
        <div className="rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-3 py-2 text-[12.5px] text-fg-muted">
          Подборка {t.owner_name ?? "коллеги"} — только просмотр: референсы в библиотеку брать можно, слова и сборы
          ведёт владелец.
        </div>
      )}

      {t.batch ? (
        <BatchBar
          batch={t.batch}
          onCancel={editable ? () => cancelBatch.mutate(t.batch!.id) : undefined}
          cancelling={cancelBatch.isPending}
        />
      ) : null}

      <ErrorLine
        error={collect.error ?? state.error ?? pinterest.error ?? cancelBatch.error ?? phraseEn.error}
      />

      <Panel
        title={
          <>
            {personal ? "Слова" : "Типы"} <span className="tnum font-normal text-fg-faint">{activeTypes}</span>
          </>
        }
        aside={editable ? addButton : null}
        annot="типы"
      >
        {notice ? (
          <div className="border-b border-border bg-ok-bg px-3 py-2 text-[12.5px] text-ok">{notice}</div>
        ) : null}
        {t.types.length === 0 && editable ? (
          <Empty>
            <div className="grid justify-items-center gap-2">
              {personal
                ? "Слов пока нет. Добавьте слова — и запускайте по ним сбор с Pinterest прямо из таблицы."
                : "Типов пока нет — добавьте свои или сделайте тип из фразы Wordstat ниже."}
              {addButton}
            </div>
          </Empty>
        ) : (
          <TypesTable
            types={t.types}
            pendingId={state.isPending ? (state.variables?.id ?? null) : null}
            onState={editable ? (id, value) => state.mutate({ id, value }) : undefined}
            onCollect={editable ? (id) => pinterest.mutate(id) : undefined}
            onPhraseEn={editable ? (id, text) => phraseEn.mutate({ id, text }) : undefined}
            collectingIds={collectingIds}
            noun={personal ? "слово" : "тип"}
            emptyText={personal ? "Слов в подборке пока нет." : "Типов пока нет."}
          />
        )}
      </Panel>

      {t.tasks.length ? (
        <Panel
          title={
            <>
              Сбор
              {active.length ? <span className="ml-2 font-normal text-warn">идёт {active.length}</span> : null}
            </>
          }
          annot="задачи тематики"
        >
          <TaskList tasks={t.tasks} limit={active.length ? Math.max(3, active.length) : 3} />
        </Panel>
      ) : null}

      {t.phrase ? (
        <Panel title="Фразы Wordstat" annot="фразы">
          {t.phrases.length === 0 && t.associations.length === 0 ? (
            <Empty>Фраз пока нет — нажмите «Обновить из Wordstat».</Empty>
          ) : (
            <PhrasesPanel topic={t} readOnly={!editable} onTypeAdded={(name) => onAdded([name])} />
          )}
        </Panel>
      ) : null}

      <Dialog open={confirmAll} title="Собрать все референсы (ru + en)" onClose={closeConfirmAll}>
        <div className="grid gap-2 px-4 py-3 text-[13px] text-fg">
          <p>
            Поисков: <span className="tnum font-medium">{t.batch_todo}</span>, примерно {durationText(batchMinutes)} (≈7
            мин на поиск). Уже собранные пропустим. Фразы без английского варианта переведём автоматически.
          </p>
          <ErrorLine error={pinterestAll.error} />
        </div>
        <DialogFooter>
          <button type="button" className={btnGhost} onClick={closeConfirmAll}>
            Отмена
          </button>
          <button
            type="button"
            className={btnPrimary}
            disabled={pinterestAll.isPending}
            onClick={() => pinterestAll.mutate()}
          >
            {pinterestAll.isPending ? <Loader2 size={13} className="animate-spin" /> : null}
            Запустить
          </button>
        </DialogFooter>
      </Dialog>

      <Dialog open={adding} title={personal ? "Добавить слова" : "Добавить типы"} onClose={() => setAdding(false)}>
        <AddWordForm
          topic={t}
          onCancel={() => setAdding(false)}
          onAdded={(names, complete) => {
            onAdded(names);
            if (complete) setAdding(false);
          }}
        />
      </Dialog>
    </div>
  );
}

/** 95 мин → «1 ч 35 мин»; без часов — «35 мин». */
function durationText(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h} ч ${m} мин` : `${m} мин`;
}

